import { useState } from 'react';
import { ArrowRight, Play, Monitor, CheckCircle, Users, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import dashboard from "../assets/Capture.PNG"

const HeroSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section className="relative w-full min-h-screen bg-zinc-950 text-white flex flex-col justify-center pt-32 overflow-hidden pb-24">

      {/* Background Lighting Glows */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[50%] bg-blue-600/10 rounded-full blur-[160px] pointer-events-none"></div>
      <div className="absolute bottom-[0%] right-[-10%] w-[50%] h-[60%] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none"></div>

      <div className="w-full px-6 sm:px-12 lg:px-16 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">

          {/* --- LEFT SIDE: Core Restaurant Value --- */}
          <div className="flex-1 text-center lg:text-left space-y-8">

            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-800 shadow-xl text-blue-400 text-xs font-bold uppercase tracking-wide">
              <span>SOKO Restaurant OS by Top Orion</span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] uppercase">
              Run Every Branch.<br />
              Track Every Ingredient.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400">
                Stop the Waste.
              </span>
            </h1>

            <p className="text-lg text-zinc-300 max-w-xl mx-auto lg:mx-0 font-medium leading-relaxed font-sans">
              Stop guessing your weekly profits. SOKO brings your kitchen orders, stock rooms, staff schedules, and daily sales from all your restaurant locations into one clean dashboard.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-blue-500 text-white rounded-xl font-black text-sm uppercase tracking-wider shadow-lg shadow-blue-500/10 hover:bg-blue-400 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                <span>Book a Free Demo</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-zinc-900 text-zinc-200 border-2 border-zinc-800 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-zinc-800 hover:border-zinc-700 transition-all"
              >
                <Play className="w-4 h-4 text-cyan-400 fill-current" />
                <span>See How It Works</span>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-zinc-900 max-w-lg mx-auto lg:mx-0">
              <div>
                <p className="text-2xl sm:text-3xl font-black text-white">500+</p>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-1">Restaurants Using Us</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-cyan-400">30%</p>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-1">Less Food Waste</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black text-white">100%</p>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-1">Real-Time Control</p>
              </div>
            </div>

          </div>

          {/* --- RIGHT SIDE: Dashboard Mockup Preview Window --- */}
          <div className="flex-1 relative w-full flex justify-center">
            <div className="relative z-10 w-full max-w-2xl group">

              <div className="absolute inset-0 bg-blue-500/10 rounded-2xl blur-2xl opacity-40 group-hover:opacity-60 transition-opacity duration-500"></div>

              <div className="w-full  bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">

                <div className="bg-zinc-950 border-b border-zinc-800/80 px-4 py-3 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-zinc-800"></span>
                    <span className="w-3 h-3 rounded-full bg-zinc-800"></span>
                    <span className="w-3 h-3 rounded-full bg-zinc-800"></span>
                  </div>
                  <div className="px-4 py-1 bg-zinc-900 border border-zinc-800 text-[10px] text-zinc-400 rounded-md flex items-center gap-1.5 w-48 justify-center">
                    <span>soko-app-preview</span>
                  </div>
                  <div className="w-10"></div>
                </div>
                <div className="bg-zinc-950 relative overflow-hidden">
                  <img
                    src={dashboard}
                    alt="Dashboard Preview"
                    className="w-full h-auto object-contain rounded-lg border border-zinc-800 shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent pointer-events-none"></div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </div>

      {/* --- Action Popup Modal --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full shadow-2xl text-center space-y-6">

            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>

            <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded-xl flex items-center justify-center mx-auto">
              <Users size={28} />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-bold text-white uppercase tracking-tight">Talk to a System Expert</h3>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Leave your contact details and we will show you exactly how SOKO can fit your specific restaurant setup and branch locations.
              </p>
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2.5 text-xs text-zinc-400 text-left bg-zinc-950 p-2.5 rounded-lg border border-zinc-800">
                <CheckCircle className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>See live data from a mock restaurant setup</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-zinc-400 text-left bg-zinc-950 p-2.5 rounded-lg border border-zinc-800">
                <CheckCircle className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Get an exact monthly price for your business size</span>
              </div>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="w-full py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-lg"
            >
              Connect With Us
            </button>
          </div>
        </div>
      )}

    </section>
  );
};

export default HeroSection;